/**
 * Edit a single transaction (FIN-10). Date / description / amount / category /
 * notes. Transfer legs can't have their amount or date changed (the API
 * returns 409 transfer_leg_immutable), so those fields are locked for legs.
 */

import { useState } from 'react';
import { FormModal } from '../FormModal';
import {
  usePatchFinanceTransaction,
  useMoveFinanceTransactions,
  type FinanceTransactionDto,
  type FinanceCategoryDto,
  type FinanceAccountDto,
  type TransactionPatchInput,
} from '../../api/finance';
import { accountMoveTargets, financeErrorMessage } from '../../screens/finance/finance-format';
import { CategoryPicker } from './CategoryPicker';
import formStyles from '../FormModal.module.css';

export function TransactionEditModal({
  transaction,
  categories,
  accounts = [],
  onClose,
  onDelete,
}: {
  transaction: FinanceTransactionDto | null;
  categories: FinanceCategoryDto[];
  /** All accounts — used to offer "move to" the current account's parent/children. */
  accounts?: FinanceAccountDto[];
  onClose: () => void;
  /** Invoked when the user asks to delete; the parent shows the confirm + archives. */
  onDelete?: (() => void) | undefined;
}) {
  const patch = usePatchFinanceTransaction();
  const move = useMoveFinanceTransactions();
  const open = !!transaction;
  const isLeg = !!transaction?.transferId;
  const moveTargets = transaction ? accountMoveTargets(accounts, transaction.accountId) : [];

  const [date, setDate] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [notes, setNotes] = useState('');
  const [seen, setSeen] = useState<string | null>(null);

  if (open && transaction.id !== seen) {
    setSeen(transaction.id);
    setDate(transaction.date);
    setDescription(transaction.description);
    setAmount(String(transaction.amount));
    setCategoryId(transaction.categoryId ?? '');
    setNotes(transaction.notes);
    patch.reset();
  }
  if (!open && seen !== null) setSeen(null);

  const amountNum = Number(amount);
  const valid =
    description.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(date) &&
    Number.isFinite(amountNum) &&
    amountNum !== 0;

  const submit = () => {
    if (!transaction || !valid || patch.isPending) return;
    const next: TransactionPatchInput = {
      description: description.trim(),
      categoryId: categoryId || null,
      notes,
    };
    if (!isLeg) {
      next.date = date;
      next.amount = amountNum;
    }
    patch.mutate({ id: transaction.id, patch: next }, { onSuccess: onClose });
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title="Edit transaction"
      subtitle={isLeg ? 'Transfer leg — amount and date are locked. Unlink the transfer to change them.' : undefined}
      submitLabel={patch.isPending ? 'Saving…' : 'Save'}
      submitDisabled={!valid || patch.isPending}
      onSubmit={submit}
      error={financeErrorMessage(patch.error)}
      destructiveAction={
        !isLeg && onDelete ? { label: 'Delete', onClick: onDelete } : undefined
      }
    >
      <div className={formStyles.field}>
        <div className={formStyles.row}>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="tx-date">Date</label>
            <input
              id="tx-date"
              type="date"
              className={formStyles.input}
              value={date}
              disabled={isLeg}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div>
            <label className={formStyles.fieldLabel} htmlFor="tx-amount">Amount (native)</label>
            <input
              id="tx-amount"
              className={formStyles.input}
              inputMode="decimal"
              value={amount}
              disabled={isLeg}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="tx-desc">Description</label>
        <input
          id="tx-desc"
          className={formStyles.input}
          value={description}
          maxLength={500}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div className={formStyles.field}>
        <span className={formStyles.fieldLabel}>Category</span>
        <CategoryPicker
          categories={categories}
          value={categoryId || null}
          kind={transaction && transaction.amount < 0 ? 'expense' : 'income'}
          placeholder="Uncategorized"
          ariaLabel="Category"
          disabled={isLeg}
          onChange={(catId) => setCategoryId(catId)}
        />
      </div>

      <div className={formStyles.field}>
        <label className={formStyles.fieldLabel} htmlFor="tx-notes">Notes</label>
        <textarea
          id="tx-notes"
          className={formStyles.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {!isLeg && moveTargets.length > 0 && (
        <div className={formStyles.field}>
          <label className={formStyles.fieldLabel} htmlFor="tx-move">
            Move to account
          </label>
          <select
            id="tx-move"
            className={formStyles.input}
            value=""
            disabled={move.isPending}
            onChange={(e) => {
              const targetAccountId = e.target.value;
              if (!targetAccountId || !transaction) return;
              move.mutate(
                { transactionIds: [transaction.id], targetAccountId },
                { onSuccess: onClose },
              );
            }}
          >
            <option value="">{move.isPending ? 'Moving…' : 'Move to…'}</option>
            {moveTargets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <span className={formStyles.fieldHint}>
            {financeErrorMessage(move.error) ??
              'Reassigns this transaction to the parent or a sub-account.'}
          </span>
        </div>
      )}
    </FormModal>
  );
}
